import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Webhook, Trash2, Loader2, CheckCircle, XCircle,
  Bell, Mail, ArrowRight, Shield, Copy, RefreshCw,
  Link2, TestTube, Clock, ChevronDown, ChevronRight,
  Zap, Terminal, ExternalLink, AlertTriangle
} from 'lucide-react';

/* ─── Notification Tier Selector ─── */
const tierDotColors = { beginner: 'bg-emerald-400', standard: 'bg-blue-400', advanced: 'bg-amber-400' };

const TierCard = ({ level, title, desc, features, active, onSelect, colorClass }) => (
  <button
    onClick={() => onSelect(level)}
    className={`p-4 rounded-xl border text-left transition-all w-full ${
      active ? `${colorClass} bg-primary/5` : 'border-border bg-card hover:border-muted-foreground/30'
    }`}
    data-testid={`tier-${level}`}
  >
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-2 h-2 rounded-full ${active ? tierDotColors[level] : 'bg-muted-foreground/40'}`} />
      <h3 className={`text-sm font-semibold ${active ? 'text-foreground' : 'text-muted-foreground'}`}>{title}</h3>
    </div>
    <p className={`text-[11px] mb-3 ${active ? 'text-muted-foreground' : 'text-muted-foreground/80'}`}>{desc}</p>
    <div className="space-y-1.5">
      {features.map((f, i) => (
        <div key={i} className="flex items-center gap-2">
          <CheckCircle className={`h-3 w-3 shrink-0 ${active ? 'text-emerald-500' : 'text-muted-foreground/50'}`} />
          <span className={`text-[11px] ${active ? 'text-foreground/80' : 'text-muted-foreground/80'}`}>{f}</span>
        </div>
      ))}
    </div>
  </button>
);

/* ─── Webhook Endpoint Row ─── */
const WebhookRow = ({ webhook, onTest, onDelete, testingId }) => {
  const [showSecret, setShowSecret] = useState(false);
  return (
    <div className="p-4 bg-card border border-gray-200 dark:border-white/10 rounded-xl shadow-sm" data-testid={`webhook-row-${webhook.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-muted border border-border flex items-center justify-center shrink-0 mt-0.5">
            <Webhook className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-mono text-foreground truncate">{webhook.url}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <button
                onClick={() => setShowSecret(!showSecret)}
                className="text-[11px] text-muted-foreground hover:text-foreground transition-colors font-mono"
              >
                Secret: {showSecret ? webhook.secret : `${webhook.secret?.slice(0, 12)}...`}
              </button>
              <button
                onClick={() => { navigator.clipboard.writeText(webhook.secret); toast.success('Secret copied'); }}
                className="text-muted-foreground/60 hover:text-muted-foreground"
              >
                <Copy className="h-3 w-3" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {webhook.events?.map((e) => (
                <span key={e} className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-background border border-gray-200 dark:border-white/10 text-muted-foreground">{e}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2.5 text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={() => onTest(webhook.id)}
            disabled={testingId === webhook.id}
            data-testid={`test-webhook-${webhook.id}`}
          >
            {testingId === webhook.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TestTube className="h-3.5 w-3.5" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2.5 text-red-400/60 hover:text-red-500 hover:bg-red-500/10"
            onClick={() => onDelete(webhook.id)}
            data-testid={`delete-webhook-${webhook.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ─── */
const MerchantWebhooks = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [form, setForm] = useState({ url: '', events: ['payment.confirmed', 'invoice.paid', 'invoice.expired', 'payment.detected'] });
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [securityExpanded, setSecurityExpanded] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [whRes, logsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/webhooks`),
        axios.get(`${API}/webhooks/logs`),
        axios.get(`${API}/notifications/settings`),
      ]);
      setWebhooks(whRes.data);
      setLogs(logsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (newSettings) => {
    setSavingSettings(true);
    try {
      const resp = await axios.put(`${API}/notifications/settings`, newSettings);
      setSettings(resp.data);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const selectTier = (level) => {
    const updated = {
      ...settings,
      notification_level: level,
      auto_webhook_relay: level === 'standard' || level === 'advanced',
      custom_webhook_enabled: level === 'advanced',
    };
    setSettings(updated);
    handleSaveSettings(updated);
  };

  const toggleSetting = (key) => {
    const updated = { ...settings, [key]: !settings[key] };
    setSettings(updated);
    handleSaveSettings(updated);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/webhooks`, { url: form.url, events: form.events });
      toast.success('Webhook endpoint added');
      setDialogOpen(false);
      setForm({ url: '', events: ['payment.confirmed', 'invoice.paid', 'invoice.expired', 'payment.detected'] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (webhookId) => {
    if (!confirm('Remove this webhook endpoint?')) return;
    try {
      await axios.delete(`${API}/webhooks/${webhookId}`);
      toast.success('Webhook removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTest = async (webhookId) => {
    setTestingId(webhookId);
    try {
      const response = await axios.post(`${API}/webhooks/${webhookId}/test`);
      if (response.data.status === 'delivered') {
        toast.success(`Delivered (${response.data.response_code})`);
      } else {
        toast.error(`Failed: ${response.data.error || 'Unknown error'}`);
      }
      fetchData();
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTestingId(null);
    }
  };

  const retrySchedule = ['Immediate', '30 seconds', '5 minutes', '30 minutes', '2 hours', '24 hours'];
  const tier = settings?.notification_level || 'beginner';

  if (loading) {
    return (
      <MerchantLayout title="Payment Notifications">
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
        </div>
      </MerchantLayout>
    );
  }

  return (
    <MerchantLayout title="Payment Notifications">
      <div className="space-y-6 max-w-4xl" data-testid="webhooks-page">

        {/* Notification Tiers */}
        <div>
          <div className="flex items-center gap-2 mb-3 px-1">
            <Bell className="h-3.5 w-3.5 text-muted-foreground" />
            <h2 className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Notification Level</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <TierCard
              level="beginner"
              title="Beginner"
              desc="No backend required"
              features={['Redirect after payment', 'Email notifications', 'Dashboard updates']}
              active={tier === 'beginner'}
              onSelect={selectTier}
              colorClass="border-emerald-400/30"
            />
            <TierCard
              level="standard"
              title="Standard"
              desc="Auto webhook relay"
              features={['All beginner features', 'Auto-hosted webhook relay', 'Zapier / Make.com ready']}
              active={tier === 'standard'}
              onSelect={selectTier}
              colorClass="border-blue-400/30"
            />
            <TierCard
              level="advanced"
              title="Advanced"
              desc="Full control"
              features={['All standard features', 'Custom webhook endpoint', 'HMAC SHA256 signing']}
              active={tier === 'advanced'}
              onSelect={selectTier}
              colorClass="border-amber-400/30"
            />
          </div>
        </div>

        {/* Default Notification Settings */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-4">Default Notifications</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-foreground">Redirect after payment</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Customer returns to your success/cancel URL</p>
                </div>
              </div>
              <Switch
                checked={settings?.redirect_after_payment ?? true}
                onCheckedChange={() => toggleSetting('redirect_after_payment')}
                data-testid="toggle-redirect"
              />
            </div>
            <div className="h-px bg-muted" />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-foreground">Email notifications</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Receive email on payment confirmation</p>
                </div>
              </div>
              <Switch
                checked={settings?.email_notifications ?? true}
                onCheckedChange={() => toggleSetting('email_notifications')}
                data-testid="toggle-email"
              />
            </div>

            {/* Redirect URLs */}
            {settings?.redirect_after_payment && (
              <>
                <div className="h-px bg-muted" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] text-muted-foreground uppercase tracking-wider">Success URL</Label>
                    <Input
                      placeholder="https://yoursite.com/success"
                      value={settings?.success_url || ''}
                      onChange={(e) => setSettings({ ...settings, success_url: e.target.value })}
                      onBlur={() => handleSaveSettings(settings)}
                      className="mt-1.5 bg-transparent border-border h-9 text-xs font-mono text-foreground placeholder:text-muted-foreground/60"
                      data-testid="success-url-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground uppercase tracking-wider">Cancel URL</Label>
                    <Input
                      placeholder="https://yoursite.com/cancel"
                      value={settings?.cancel_url || ''}
                      onChange={(e) => setSettings({ ...settings, cancel_url: e.target.value })}
                      onBlur={() => handleSaveSettings(settings)}
                      className="mt-1.5 bg-transparent border-border h-9 text-xs font-mono text-foreground placeholder:text-muted-foreground/60"
                      data-testid="cancel-url-input"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Webhook Endpoints (Advanced) */}
        {(tier === 'advanced' || webhooks.length > 0) && (
          <div className="pt-4">
            <div className="flex items-center justify-between mb-6 px-1">
              <div className="flex items-center gap-2">
                <Webhook className="h-3.5 w-3.5 text-muted-foreground" />
                <h2 className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Webhook Endpoints</h2>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    size="sm"
                    className="h-8 bg-emerald-500 hover:bg-emerald-400 text-black font-medium text-xs rounded-lg"
                    data-testid="create-webhook-btn"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" />Add Endpoint
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle className="font-['Inter'] text-foreground">Add Webhook Endpoint</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                      <Label className="text-[11px] text-muted-foreground uppercase tracking-wider">Endpoint URL</Label>
                      <Input
                        type="url"
                        placeholder="https://your-site.com/webhooks/coinpayup"
                        value={form.url}
                        onChange={(e) => setForm({ ...form, url: e.target.value })}
                        required
                        className="mt-1.5 bg-transparent border-border text-sm font-mono text-foreground placeholder:text-muted-foreground/60"
                        data-testid="webhook-url-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2 block">Events</Label>
                      <div className="space-y-2">
                        {['payment.detected', 'payment.confirmed', 'invoice.paid', 'invoice.expired'].map((evt) => (
                          <label key={evt} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={form.events.includes(evt)}
                              onChange={(e) => {
                                if (e.target.checked) setForm({ ...form, events: [...form.events, evt] });
                                else setForm({ ...form, events: form.events.filter(x => x !== evt) });
                              }}
                              className="rounded border-border bg-transparent text-emerald-500 focus:ring-emerald-500/30"
                            />
                            <code className="text-xs font-mono text-muted-foreground">{evt}</code>
                          </label>
                        ))}
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-10 rounded-lg"
                      disabled={creating}
                      data-testid="webhook-submit-btn"
                    >
                      {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Endpoint'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {webhooks.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 dark:border-white/10 rounded-2xl py-12 text-center">
                <Webhook className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No webhook endpoints configured</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Add an endpoint to receive real-time payment notifications</p>
              </div>
            ) : (
              <div className="space-y-2">
                {webhooks.map((wh) => (
                  <WebhookRow key={wh.id} webhook={wh} onTest={handleTest} onDelete={handleDelete} testingId={testingId} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Webhook Security Reference */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <button
            onClick={() => setSecurityExpanded(!securityExpanded)}
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted transition-colors"
            data-testid="security-toggle"
          >
            <div className="flex items-center gap-2.5">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Webhook Security & Verification</span>
            </div>
            {securityExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </button>
          {securityExpanded && (
            <div className="px-5 pb-5 border-t border-border pt-4 space-y-5">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Every webhook delivery includes HMAC SHA256 signatures. Verify the signature to ensure the payload is authentic and untampered.
              </p>
              <div>
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider mb-3 font-medium">Headers Included</p>
                <div className="space-y-3">
                  {[
                    { header: 'X-CoinPayUp-Signature', desc: 'HMAC SHA256 of payload body' },
                    { header: 'X-CoinPayUp-Event', desc: 'Event type (e.g., payment.confirmed)' },
                    { header: 'X-CoinPayUp-Delivery-ID', desc: 'Unique delivery identifier (UUID)' },
                  ].map((h) => (
                    <div key={h.header} className="flex items-center gap-4 px-4 py-3.5 bg-background border border-gray-200 dark:border-white/10 rounded-lg shadow-sm">
                      <code className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400 shrink-0 font-semibold">{h.header}</code>
                      <span className="text-[11px] text-muted-foreground">{h.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider mb-3 font-medium">Verification (PHP)</p>
                <pre className="p-4 bg-background rounded-lg border border-gray-200 dark:border-white/10 shadow-sm overflow-x-auto">
                  <code className="text-[12px] font-mono text-muted-foreground leading-relaxed">{`$expected = hash_hmac('sha256', $payload, $secret);
if (!hash_equals($expected, $signature)) {
    http_response_code(401);
    die('Invalid signature');
}`}</code>
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Retry Logic */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
            <h3 className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Retry Schedule</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {retrySchedule.map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="px-2.5 py-1 rounded-lg bg-background border border-gray-200 dark:border-white/10 text-[11px] font-mono text-muted-foreground">{step}</span>
                {i < retrySchedule.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground/50" />}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Delivery stops after first successful response (2xx).</p>
        </div>

        {/* Webhook Payload Preview */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Terminal className="h-3.5 w-3.5 text-muted-foreground" />
            <h3 className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Payload Structure</h3>
          </div>
          <pre className="p-4 bg-background rounded-xl border border-gray-200 dark:border-white/10 shadow-sm overflow-x-auto">
            <code className="text-[12px] font-mono text-muted-foreground leading-relaxed">{JSON.stringify({
              event: "invoice.paid",
              data: {
                invoice_id: "inv_123",
                amount: 100.00,
                currency: "USDT",
                tx_hash: "0xabc...",
                confirmations: 12,
                status: "confirmed"
              },
              timestamp: "2026-02-19T12:00:00Z"
            }, null, 2)}</code>
          </pre>
        </div>

        {/* Delivery Logs */}
        {logs.length > 0 && (
          <div>
            <button
              onClick={() => setLogsExpanded(!logsExpanded)}
              className="flex items-center gap-2 mb-3 px-1 group"
              data-testid="logs-toggle"
            >
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <h2 className="text-xs text-muted-foreground uppercase tracking-wider font-medium group-hover:text-foreground transition-colors">
                Delivery Logs ({logs.length})
              </h2>
              {logsExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
            </button>
            {logsExpanded && (
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="divide-y divide-border">
                  {logs.slice(0, 25).map((log) => (
                    <div key={log.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted" data-testid={`webhook-log-${log.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {log.status === 'delivered' ? (
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-foreground">{log.event}</code>
                            {log.invoice_id && (
                              <span className="text-[10px] font-mono text-muted-foreground">inv:{log.invoice_id.slice(0, 8)}</span>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {new Date(log.timestamp).toLocaleString()}
                            {log.attempt && log.attempt > 1 && (
                              <span className="ml-2 text-amber-500">Attempt {log.attempt}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full shrink-0 ${
                        log.status === 'delivered'
                          ? 'bg-emerald-500/10 text-emerald-500'
                          : 'bg-red-500/10 text-red-500'
                      }`}>
                        {log.response_code || log.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </MerchantLayout>
  );
};

export default MerchantWebhooks;
